"use client";
/**
 * Share-the-bill action (spec 2026-07-22 §5.2).
 *
 * Calls `POST .../bill-invite` for the table this diner scanned into, then
 * shares `https://<origin>/?inviteCode=ABCD` via `navigator.share`, falling
 * back to a clipboard copy + inline confirmation where it isn't available
 * (mirrors ReferralsScreen's share/copy pattern).
 *
 * The link invites people to view and pay the TABLE'S BILL — the recipient
 * side is the already-shipped `?inviteCode=` → GroupJoinScreen flow, so
 * nothing new is needed there.
 *
 * Renders nothing without table context (branchId + tableId from the QR):
 * there is no table whose bill we could invite anyone to.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui";
import { Icons } from "@/components/Icons";
import { ApiError } from "@/api/http";
import { billInviteApi, buildBillInviteUrl } from "@/api/bill-invite";
import { useTable } from "@/store/table";
import { payerForOrder } from "@/lib/payer-token";
import { readParticipant } from "@/lib/participant-token";

interface ShareBillButtonProps {
  /** Order whose bill is being shared — only used to pick the best display name. */
  orderId?: string | null;
  /**
   * Fallback name for the roster when there's no payer/participant session
   * yet (e.g. the signed-in customer's name). Passed in rather than read from
   * `useAuth()` so this component has no auth-context dependency.
   */
  displayNameHint?: string | null;
  variant?: "primary" | "outline" | "soft" | "ghost" | "dark";
  size?: "sm" | "md" | "lg";
  full?: boolean;
}

export function ShareBillButton({
  orderId,
  displayNameHint,
  variant = "outline",
  size = "md",
  full,
}: ShareBillButtonProps) {
  const { t } = useTranslation();
  const { tableInfo } = useTable();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const branchId = tableInfo?.branchId ?? null;
  const tableId = tableInfo?.tableId ?? null;
  if (!branchId || !tableId) return null;

  const displayName =
    payerForOrder(orderId ?? null)?.displayName ??
    readParticipant()?.displayName ??
    displayNameHint ??
    t("v2.share_bill.default_name");

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 2500);
  };

  const onShare = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    let url: string;
    try {
      const res = await billInviteApi.create(branchId, tableId, { displayName });
      url = buildBillInviteUrl(res.inviteCode);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      setError(
        status === 404
          ? t("v2.share_bill.no_active_order")
          : status === 409
            ? t("v2.share_bill.conflict")
            : t("v2.share_bill.error"),
      );
      setBusy(false);
      return;
    }

    // Sharing is best-effort: a cancelled share sheet or a blocked clipboard
    // must never surface as an error — the invite itself already succeeded.
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({
          title: t("v2.share_bill.share_title"),
          text: t("v2.share_bill.share_text"),
          url,
        });
      } else {
        await navigator.clipboard?.writeText(url);
        flash(t("v2.share_bill.copied"));
      }
    } catch {
      /* cancelled / clipboard blocked — no-op */
    }
    setBusy(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: full ? 1 : undefined }}>
      <Button
        variant={variant}
        size={size}
        full={full}
        disabled={busy}
        onClick={() => void onShare()}
        leading={<Icons.share size={15} />}
      >
        {busy ? t("v2.share_bill.sharing") : t("v2.share_bill.cta")}
      </Button>
      {notice && (
        <div
          role="status"
          style={{ fontSize: 12, fontWeight: 600, color: "var(--primary-700)", textAlign: "center" }}
        >
          {notice}
        </div>
      )}
      {error && (
        <div
          role="alert"
          style={{ fontSize: 12, fontWeight: 600, color: "#b91c1c", textAlign: "center" }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
